<template>
  <TileCard id="calendar-table" class="calendar-table-card" :loaded="!calendarStore.getAllLoaded" >
    <template #body>
      <DxDataGrid
        :data-source="gridData"
        :show-borders="true"
        :hover-state-enabled="true"
        :column-auto-width="false"
        :row-alternation-enabled="true"
        :word-wrap-enabled="false"
        :columns="columns"
        key-expr="id"
        id="calendarGrid"
        :style="getGridWidth()"
        @row-expanding="loadReportTable"
        :cell-hint-enabled="true"
        @content-ready="onContentReady"
        ref="calendarGrid">
        <DxMasterDetail :enabled="canShowDetail()"  template="masterDetailTemplate" />
        <DxStateStoring :enabled="true" type="localStorage" storage-key="calendarGrid" />
        <DxScrolling mode="standard"/>
        <DxFilterRow :visible="true" />
        <DxHeaderFilter :visible="true" />
      
        <template #masterDetailTemplate="{ data }">
          <div v-if="state.loadDetail">
            <TileCard id="report-table" :loaded="!reportingStore.getAllLoaded">
              <template #body>
                <ReportTable :reportingMonth="reportingStore.getAll" :state.="state" @update:model-value="loadReportTableData" :readonly="true">
                  <template #buttons></template>
                </ReportTable>
              </template>             
            </TileCard>
          </div>
        </template>  
        <DxPaging :page-size="10" />
        <DxPager :show-page-size-selector="true" :allowed-page-sizes="[10, 20, 30]" />
        <template #tl-cell-template="{ data }">
          <div class="photo-template">
            <img :src="createBlobUrl(data.data)" alt="Direct manager" class="person-photo person-photo--rounded"
              :title="data.data?.teamLeader?.fullName" />
          </div>
        </template>      
        <template #cellTemplate="{ data }">
          <CalendarDay :dayData="data.value" />
        </template>
        <template #headerCellTemplate="{ data }">
          <div class="header-cell">
            {{ data.column.caption }}
          </div>
        </template>
      </DxDataGrid>
    </template>
  </TileCard>
</template>

<script setup lang="ts">
import { computed, reactive, ref, onBeforeMount, watch } from "vue";
import TileCard from "@/components/util/TileCard.vue";
import CalendarDay from "@/components/Calendar/CalendarDay.vue";
import { useCalendarStore } from "@/stores/calendar";
import DxDataGrid, { DxPaging, DxPager, DxMasterDetail, DxDataGridTypes, DxScrolling, DxStateStoring, DxFilterRow, DxHeaderFilter } from "devextreme-vue/data-grid";
import { CalendarLine } from "@/services/entities/Reporting/CalendarLine";
import { CalendarDayModel } from "@/services/entities/Reporting/CalendarDayModel";
import ReportTable from "../tables/ReportTable.vue";
import { useReportingMonthStore } from "@/stores/reporting";
import { UserRole } from "@/services/entities/UserItem";
import { useUserStore } from "@/stores/user";
import { useAzureStore } from "@/stores/azure";
import { useServiceTeamsStore } from "@/stores/serviceTeams";
import { Authorization } from "@/services/authenticate";
import { configureDataGridColumnsOrder } from "@/common/util";


const calendarStore = useCalendarStore();
const reportingStore = useReportingMonthStore();
const userStore = useUserStore();
const azureStore = useAzureStore();
const teamsStore = useServiceTeamsStore();


const props = defineProps<{
  date: { year: number; month: number }
}>();

onBeforeMount(async () => {
  await teamsStore.loadServiceTeams();
});


watch(() => calendarStore.getAllLoaded, async (v) => {
  if (!v) {
    if (gridData.value.length > 0) {
      console.log('TLIds', gridData.value.map((a) => a.teamLeaderId));
      await azureStore.loadUserPhotos(gridData.value.map((a) => a.teamLeaderId));
      gridData.value.forEach((a: any) => {
        a.photo = azureStore.getUsersPhotos.find((p: any) => p.id === a.teamLeaderId)?.blob;
      });
    }    
  }
})


const calendarGrid = ref()
let isConfigured = false;

const state = reactive({
  loadDetail: false,
  id: "" as string,
});

const columns = computed(() => {
  const cols = [
    {
      dataField: "teamMember",
      caption: "Team Member",
      fixed: "sticky",
      cssClass: 'name-cell',
      columnAutoWidth: true,
      allowFiltering: true,
    },
    {
      dataField: "teamLeader.fullName",
      caption: "TL",
      cellTemplate: "tl-cell-template",      
      width: "70px",
      allowFiltering: true,
    },
    {
      dataField: "teamName",
      caption: "Team",
      width: "150px",
      allowFiltering: true,
    }
  ];
  // Add a column for each day, using the current daysInMonth
  Array.from({ length: calendarStore.getAll.daysInMonth.length }, (_, i) => i + 1).forEach((day: number) => {
    cols.push({
      alignment: "center",
      allowEditing: false,
      allowExporting: false,
      allowFiltering: false,
      allowGrouping: false,
      allowHeaderFiltering: false,
      allowHiding: false,
      allowReordering: false,
      allowResizing: false,
      allowSearch: false,
      allowSorting: false,      
      dataField: 'day_' + day,
      caption: `${day}.`,
      cellTemplate: "cellTemplate",
      headerCellTemplate: "headerCellTemplate",      
      cssClass: getCellBackgroundClass(day),
    });
  });
  return cols;
});

const gridData = computed(() => {
  return (calendarStore.getAll.lines || []).map((line: CalendarLine) => {
    const row: Record<string, any> = {
      teamMember: line.teamMember.fullName,
      teamLeaderId: line.teamMember.teamLeaderId,
      teamLeader: line.teamMember.teamLeader,
      id: line.teamMember.id,
    };
    row["teamName"] = getUserServiceTeam(row);
    // Populate each day's data
    Array.from({ length: calendarStore.getAll.daysInMonth.length }, (_, i) => i + 1).forEach((day: number) => {
      row['day_' + day] = getDayData(line, day);
    });
    return row;
  });
});

function  onContentReady(e: any) {
  // ensure default column order
  const defaultOrder = ["teamMember", "teamLeader.fullName", "teamName"];    
  if (!isConfigured) {
    configureDataGridColumnsOrder(e, defaultOrder);
    isConfigured = true;
  }   
};


function getUserServiceTeam(data: any) {
  let teams = teamsStore.getServiceTeams.filter(t => t.members?.find(m => m.id === data.id) || t.teamLead?.find(tl => tl.id === data.id));    
  let teamNames = teams.map(t => t.teamName).sort();
  if (teamNames.length > 0)
    return teamNames.join(', ');
  else
    return "";
}

function canShowDetail(): boolean {
  return userStore.getUserRole === UserRole.Administrator || 
    userStore.getUserRole === UserRole.TeamLeader || 
    userStore.getUserRole === UserRole.TeamLeaderReport; 
}

function getDayData(line: CalendarLine, dayNum: number): CalendarDayModel {  
  // Return the CalendarDayModel for this day (or a default)
  return line.days[dayNum - 1] || {
    hours: 0,
    isAbsent: false,
    outOfOffice: false,
    isWorkDay: false,
    date: null,
  };
}

function getCellBackgroundClass(day: number): string {
  // Determine the background class based on the column data
  if (!calendarStore.getAll.daysInMonth[day - 1]) {
    return "is-work-dayOff base-cell"; // Default class for non-work days
  }
  return 'base-cell';
}

function getGridWidth(): string {
  var width = calendarStore.getAll.daysInMonth.length * 3 + 30; 
  return `width: min(100%, ${width}em);`;
}

async function loadReportTable(e: DxDataGridTypes.SelectionChangedEvent) {
  e.component.collapseAll(-1);
  state.id = e.key; // Store the selected user ID
  // state.detailUser = user;
  await loadReportTableData();
  calendarGrid.value?.instance?.updateDimensions();
}

async function loadReportTableData() {
  reportingStore.clearReportingMonth();
  state.loadDetail = true;
  await reportingStore.loadReportingMonth(
    props.date.year,
    props.date.month + 1,
    state.id,
  );
  calendarGrid.value?.instance?.updateDimensions();
}

const createBlobUrl = (data: any) => {
  try {
    if (data.teamLeaderId) {
      const photo = azureStore.getUsersPhotos.find((p: any) => p.id === data.teamLeaderId)?.blob;
      if (photo) {
        return URL.createObjectURL(photo);
      }
    }
  } catch (e) {
    console.error("Error creating blob URL", e);
    return null;
  }
};
</script>

<style scoped lang="scss">
#calendarGrid {  
  justify-self: center;
}
</style>

<style>
/* non scoped to be able to apply to DxDataGrid cells */ 
.is-work-dayOff {
  background-color: #ff00111a !important; /* Light red for work days important to override row styles */
}

.base-cell {
    width: 2.9em;
/* padding to override DevExtreme default */    
    padding-left: 0.1em !important; 
    padding-top: 5px !important;
    padding-bottom: 5px !important;
}

.name-cell {
  width: 15em;
}

.header-cell {
  font-weight: bold;
  text-align: center;
}
.photo-template {
  display: flex;
  justify-content: center;
  .person-photo {
    width: 1.5rem;
    height: 1.5rem;
    position: absolute;
  }
}
</style>